import { DomainAttributes, Domain } from '../types-uidefinition';
import { parsePrefixed } from './attribute-parser';
import { clonePrototype as clone } from 'clone';
import { componentDefaults } from 'app/infrastructure/ui/component-configuration';
import { RenderMode } from 'app/types-shared';
import { resolveRenderModes } from 'app/infrastructure/ui/component-registry';

/**
 * Shared component attributes default object
 * 
 * Ensures shared properties are defined
 * 
 * Pattern for key.property is: camelCasing.camelCase
 * 
 * Object path follows a pattern
 * 
 * Object paths resolves to attribute name:
 * 
 * > commonDefaultAttributes.label.text = :populated from: 'label.text'
 */
export const commonDefaultAttributes: object = {

  component: {
    visible: undefined,
    readOnly: undefined,
    required: undefined
  },

  label: {
    text: undefined,     // assigned from domain.label
    visible: undefined,
    required: undefined   // assigned from component.required
  },

  info: {
    text: undefined,
    visible: undefined,
    type: undefined       // 1: Button, 0: Text
  },

  infoLong: {
    text: undefined,
    visible: undefined,
    type: undefined       // 1: Button, 0: Text
  }

};

/**
 * Reads component attributes that applies to all UI components
 */
function sharedPropertyBuilder(config: Domain, renderMode: RenderMode) {

  const defaultProps = {
    label: {},
    info: {},
    infoLong: {}
  };

  const attributes = config.attributes;
  const sharedKeys = Object.keys(commonDefaultAttributes);
  const parsedMap = clone(commonDefaultAttributes);

  if (attributes && attributes.length) {

    sharedKeys.forEach((key) => {
      const resolvedAttributes = parsePrefixed(key, attributes);

      parsedMap[key] = {
        ...parsedMap[key],
        ...resolvedAttributes
      };
    });
  }

  const { component, ...propsFromAttributes } = parsedMap;
  const result = {
    ...defaultProps,
    ...component,
    ...propsFromAttributes
  };

  // Map label required to the value of component.required
  result.label.required = parsedMap.component.required;
  result.label.text = config.label;

  // Determine visibility based on the current RenderMode 
  result.visible = true;
  if (result.hiddenModes) {
    const renderModeList = Array.from(resolveRenderModes(renderMode));
    const nameOfCurrentModes = renderModeList.map(rm => RenderMode[rm]);
    const currentModeInList = nameOfCurrentModes.some(nameOfCurrentMode => result.hiddenModes.split(',').indexOf( nameOfCurrentMode ) > -1);
    result.visible = !currentModeInList;
  }

  // Default to visible when text is defined 
  ['label', 'info', 'infoLong'].forEach((item: any) => {
    if (result[item].visible === undefined) {
      result[item].visible = !!result[item].text;
    }
  });

  return result;
}

/**
 * Resolves a component builder and attribute prefix key for the component given
 */
function componentPropertyBuilder(componentType: number, attributes: DomainAttributes[]) {
  const builderConfig = componentDefaults[componentType];

  if (!attributes || !attributes.length) {
    return {};
  }

  if (!builderConfig || !builderConfig.attributeKey) {

    // Logging is a side effect and can only be used in development
    //debug(`ComponentType ${componentType} has no configured attribute prefix key`);

    return {};
  }

  const builderFn = parsePrefixed;
  const builder = builderFn.bind(null, builderConfig.attributeKey, attributes);

  return builder();
}

/**
 * Bridging of generic configuration to component spesific props 
 * to allow for render target independency.
 */
export class PropertyBuilder {
  private renderMode: RenderMode;

  private config: Domain;

  constructor(config: Domain, renderMode: RenderMode) {
    this.renderMode = renderMode;
    this.config = config;
  }

  get properties() {
    return {
      ...this.buildSharedProperties(),
      ...this.buildComponentProperties()
    };
  }

  public buildSharedProperties() {
    return sharedPropertyBuilder(this.config, this.renderMode);
  }

  public buildComponentProperties() {
    return componentPropertyBuilder(this.config.controlType, this.config.attributes);
  }
}
